import { BodyText, PageHeading } from '@/components/ui/typography'
import { DotBackground } from '@/components/ui/dot-background'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

interface PageLayoutProps {
  title: ReactNode
  description?: ReactNode
  eyebrow?: ReactNode
  variant?: 'standard' | 'compact' | 'workspace' | 'reference'
  headerAction?: ReactNode
  headerActionCompact?: ReactNode
  headerActionCompactPlacement?: 'inline' | 'below'
  children: ReactNode
  className?: string
  contentClassName?: string
}

export function PageLayout({
  title,
  description,
  eyebrow,
  variant = 'standard',
  headerAction,
  headerActionCompact,
  headerActionCompactPlacement = 'inline',
  children,
  className,
  contentClassName
}: PageLayoutProps) {
  const isWorkspace = variant === 'workspace'
  const isCompact = variant === 'compact'

  return (
    <div
      className={cn(
        'box-border flex min-h-[calc(100dvh-var(--product-shell-header-height,0px))] w-full max-w-full min-w-0 flex-col overflow-x-hidden',
        className
      )}
    >
      <section className="border-border/70 bg-background relative box-border w-full max-w-full min-w-0 overflow-hidden border-b">
        {!isWorkspace && (
          <>
            <div
              className={cn(
                'pointer-events-none absolute inset-0 opacity-50',
                '[background-size:36px_36px]',
                '[background-image:linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)]'
              )}
            />
            <div
              className="bg-background pointer-events-none absolute inset-0"
              style={{
                maskImage:
                  'radial-gradient(ellipse at 50% 12%, transparent 0%, transparent 24%, black 70%)'
              }}
            />
            <div className="from-background via-background/92 pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-linear-to-t to-transparent" />
          </>
        )}
        <div
          className={cn(
            'relative mx-auto flex w-full max-w-7xl min-w-0 flex-col gap-4 px-4 md:flex-row md:items-end md:justify-between md:px-6',
            isCompact || isWorkspace ? 'py-4 md:py-5' : 'py-5 md:py-7'
          )}
        >
          <div className="min-w-0 space-y-2.5">
            <div className="flex w-full min-w-0 items-start justify-between gap-3">
              <div className="min-w-0">
                {eyebrow && (
                  <div className="text-muted-foreground mb-2 text-xs font-semibold tracking-widest uppercase">
                    {eyebrow}
                  </div>
                )}
                <PageHeading
                  className={cn(
                    'leading-tight tracking-tight',
                    isCompact || isWorkspace
                      ? 'text-2xl md:text-3xl'
                      : 'text-2xl md:text-4xl',
                    variant === 'reference'
                      ? 'max-w-4xl'
                      : 'max-w-[14rem] truncate sm:max-w-none'
                  )}
                >
                  {title}
                </PageHeading>
                {description && (
                  <BodyText className="text-muted-foreground mt-2 max-w-2xl text-sm leading-6 md:text-base">
                    {description}
                  </BodyText>
                )}
              </div>
              {headerActionCompact &&
                headerActionCompactPlacement === 'inline' && (
                  <div className="flex min-h-10 shrink-0 items-center md:hidden">
                    {headerActionCompact}
                  </div>
                )}
            </div>
            {headerActionCompact &&
              headerActionCompactPlacement === 'below' && (
                <div className="mt-4 flex min-h-10 w-full min-w-0 md:hidden">
                  {headerActionCompact}
                </div>
              )}
          </div>
          {headerAction && (
            <div className="hidden min-h-[40px] shrink-0 md:flex md:items-center">
              {headerAction}
            </div>
          )}
        </div>
      </section>
      <div
        className={cn(
          'bg-background relative min-h-0 w-full max-w-full min-w-0 flex-1 overflow-x-hidden',
          contentClassName
        )}
      >
        {!isWorkspace && (
          <DotBackground
            className="pointer-events-none absolute inset-0 z-0 opacity-70"
            dotSize={22}
          />
        )}
        <div className="relative z-10 flex min-h-0 min-w-0 flex-1 flex-col">
          {children}
        </div>
      </div>
    </div>
  )
}
